/* TOP scroll entrances. Each item settles once; layout and the FV are independent. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const sectionSelector = '.klu, .ktf, .kht, .kv6, .kg7, .ks8';
  const groups = [
    { selector: '.klu__chapter, .ktf__chapter, .kht__chapter, .kg7__chapter, .ks8__chapter', kind: 'chapter', y: 10, duration: 700 },
    { selector: '.klu__heading-phrase', kind: 'heading', y: 24, delay: 80, stagger: 160, duration: 1100 },
    { selector: '.ktf__head h2, .kht__heading, .kg7__intro h2, .ks8__heading', kind: 'heading', y: 28, delay: 100, duration: 1100 },
    { selector: '.klu__intro', y: 12, delay: 360, duration: 850 },
    { selector: '.klu__product-head', y: 20, duration: 950 },
    { selector: '.klu__gallery-media--completed', kind: 'photo', y: 0, delay: 70, duration: 1500, zoom: 1.045 },
    { selector: '.klu__gallery-media--delivered', y: 6, duration: 650 },
    { selector: '.klu__summary, .klu__tastes-head', y: 12, delay: 80, duration: 850 },
    { selector: '.klu__taste', y: 14, stagger: 110, duration: 850 },
    { selector: '.ktf__media--illustration', kind: 'illustration', y: 22, scale: .975, duration: 1250 },
    { selector: '.ktf__media--noodles, .ktf__photo-window', kind: 'photo', y: 0, delay: 80, duration: 1600, zoom: 1.06 },
    { selector: '.ktf__body p', y: 14, delay: 120, stagger: 110, duration: 950 },
    { selector: '.ktf__link, .ktf__media figcaption', y: 8, delay: 150, duration: 750 },
    { selector: '.ktf__products h3', y: 16, duration: 900 },
    { selector: '.ktf__product', y: 18, stagger: 120, duration: 1000 },
    { selector: '.kht__main-media', kind: 'photo', y: 0, delay: 60, duration: 1500, zoom: 1.035 },
    { selector: '.kht__step', y: 20, stagger: 140, duration: 1000 },
    { selector: '.kht__button-wrap', y: 10, delay: 120, duration: 750 },
    { selector: '.kv6__header', y: 5, duration: 900 },
    { selector: '.kv6__card', y: 0, delay: 80, stagger: 180, duration: 900 },
    { selector: '.kv6__note', y: 0, delay: 240, duration: 880 },
    { selector: '.kg7__intro p', y: 12, delay: 150, duration: 850 },
    { selector: '.kg7__fact', y: 16, stagger: 120, duration: 900 },
    { selector: '.kg7__link', y: 8, delay: 120, duration: 750 },
    { selector: '.ks8__intro', y: 14, delay: 100, duration: 950 },
    { selector: '.ks8__media', kind: 'photo', y: 0, delay: 70, duration: 1550, zoom: 1.045 },
    { selector: '.ks8__choice', y: 14, delay: 80, stagger: 120, duration: 900 },
    { selector: '.ks8__all-wrap', y: 8, delay: 120, duration: 750 }
  ];
  const pending = new Set();
  let observer;
  let frame = 0;
  let active = false;

  const show = (element, instant = false) => {
    if (instant) element.classList.add('is-kazu-instant');
    element.classList.add('is-kazu-visible');
    pending.delete(element);
    observer?.unobserve(element);
  };

  const checkPassed = () => {
    frame = 0;
    // Hash links, fast scrolling and newly displayed gallery panels must not leave content hidden.
    pending.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      if (rect.top < window.innerHeight * .92) show(element, rect.bottom <= 0);
    });
  };
  const requestCheck = () => {
    if (active && pending.size && !frame) frame = window.requestAnimationFrame(checkPassed);
  };
  const stop = () => {
    active = false;
    observer?.disconnect();
    window.cancelAnimationFrame(frame);
    frame = 0;
    pending.forEach((element) => show(element, true));
    root.classList.remove('kazu-top-motion-capable');
  };
  const onFocus = (event) => {
    pending.forEach((element) => {
      if (element === event.target || element.contains(event.target)) show(element, true);
    });
  };

  const register = (scope = document) => {
    groups.forEach((group) => {
      const localIndices = new Map();
      scope.querySelectorAll(group.selector).forEach((element) => {
        if (element.dataset.kazuMotion !== undefined || !element.closest(sectionSelector)) return;
        const section = element.closest(sectionSelector);
        const index = localIndices.get(section) || 0;
        localIndices.set(section, index + 1);
        const delay = Math.min(440, (group.delay || 0) + (group.stagger || 0) * index);
        const duration = group.duration || 900;
        element.style.setProperty('--kazu-motion-y', `${group.y || 0}px`);
        element.style.setProperty('--kazu-motion-y-mobile', `${Math.round((group.y || 0) * .72)}px`);
        element.style.setProperty('--kazu-motion-duration', `${duration}ms`);
        element.style.setProperty('--kazu-motion-duration-mobile', `${Math.round(duration * .88)}ms`);
        element.style.setProperty('--kazu-motion-delay', `${delay}ms`);
        element.style.setProperty('--kazu-motion-delay-mobile', `${Math.min(delay, 220)}ms`);
        element.style.setProperty('--kazu-motion-scale', group.scale || 1);
        element.style.setProperty('--kazu-motion-zoom', group.zoom || 1);
        element.dataset.kazuMotion = group.kind || 'text';
        pending.add(element);
        observer.observe(element);
      });
    });
    requestCheck();
  };

  const init = () => {
    if (!document.querySelector('main [data-kazu-fv22]') || reduceMotion.matches || !('IntersectionObserver' in window)) {
      root.classList.remove('kazu-top-motion-capable');
      return;
    }
    try {
      active = true;
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) show(entry.target); });
      }, { rootMargin: '0px 0px -8% 0px', threshold: .01 });
      root.classList.add('kazu-top-motion-capable');
      register();
      root.classList.add('kazu-top-motion-ready');
      window.addEventListener('scroll', requestCheck, { passive: true });
      window.addEventListener('resize', requestCheck, { passive: true });
      window.addEventListener('pageshow', requestCheck);
      document.addEventListener('focusin', onFocus);
      // Reveal once, even if the OS preference is changed during the visit.
      reduceMotion.addEventListener('change', (event) => { if (event.matches) stop(); });
      mobile.addEventListener('change', requestCheck);
      document.addEventListener('shopify:section:load', (event) => {
        if (active) { register(event.target); requestCheck(); }
      });
    } catch (error) {
      stop();
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
